import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  Typography,
  ButtonGroup,
  ButtonGroupTypes,
  FormInput,
} from '@ecdlink/ui';
import { useForm, useFormState } from 'react-hook-form';
import { useState, useEffect } from 'react';
import {
  MotherContactInformationProps,
  yesNoOptions,
} from './mother-contact-information.types';
import {
  MothertContactInformationModel,
  motherContactInformationModelSchema,
} from '@/schemas/infant/mother-contact-information';

export const MotherContactInformation: React.FC<
  MotherContactInformationProps
> = ({ onSubmit, details, existingContactInfo }) => {
  const {
    getValues: getMotherContactInformationFormValues,
    setValue: setMotherContactInformationFormValue,
    register: motherFormRegister,
    control: momContactInformationControl,
    trigger,
  } = useForm<MothertContactInformationModel>({
    resolver: yupResolver(motherContactInformationModelSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      cellphone: existingContactInfo?.cellphone || '',
      whatsapp: existingContactInfo?.whatsapp || '',
    },
  });

  const { errors, isValid } = useFormState({
    control: momContactInformationControl,
  });

  // Determine initial hasWhatsapp state from existing data
  const determineInitialWhatsappState = () => {
    if (!existingContactInfo) return null;

    // If whatsapp field exists and is different from cellphone, they don't use same number
    if (
      existingContactInfo.whatsapp &&
      existingContactInfo.cellphone !== existingContactInfo.whatsapp
    ) {
      return false;
    }

    // If cellphone exists but no separate whatsapp, they use same number
    if (existingContactInfo.cellphone && !existingContactInfo.whatsapp) {
      return true;
    }

    // If both exist and are the same
    if (existingContactInfo.cellphone === existingContactInfo.whatsapp) {
      return true;
    }

    return null;
  };

  const [hasWhatsapp, setHasWhatsapp] = useState<any>(
    determineInitialWhatsappState()
  );

  // Initialize form values from existingContactInfo when available
  useEffect(() => {
    if (existingContactInfo) {
      if (existingContactInfo.cellphone) {
        setMotherContactInformationFormValue(
          'cellphone',
          existingContactInfo.cellphone
        );
      }
      if (existingContactInfo.whatsapp) {
        setMotherContactInformationFormValue(
          'whatsapp',
          existingContactInfo.whatsapp
        );
      }

      // Trigger validation after setting values
      trigger();
    }
  }, [existingContactInfo, setMotherContactInformationFormValue, trigger]);

  return (
    <>
      <div>
        <Typography
          type="h2"
          color={'textDark'}
          text={`${details?.name}`}
          className="pt-6"
        />
        <Typography
          type="h4"
          color={'textMid'}
          text={'Contact information'}
          className="w-11/12 pt-2"
        />
      </div>
      <div className="flex w-11/12 justify-center text-red-400">
        <Divider dividerType="dashed" />
      </div>
      <div>
        <FormInput<MothertContactInformationModel>
          label={'Cellphone number'}
          register={motherFormRegister}
          nameProp={'cellphone'}
          placeholder={'e.g 012 345 6789'}
          type={'number'}
          className="mt-4"
          value={existingContactInfo?.cellphone}
          error={!!errors.cellphone ? errors.cellphone : undefined}
        ></FormInput>
        <div className="mt-4">
          <Typography
            type="h4"
            color={'textMid'}
            text={`Does ${details?.name} use this cellphone number for WhatsApp?`}
            className="w-11/12 pt-2"
          />
          <div className="mt-2">
            <ButtonGroup<boolean>
              options={yesNoOptions}
              selectedOptions={hasWhatsapp}
              onOptionSelected={(value: boolean | boolean[]) => {
                setHasWhatsapp(value);
                setMotherContactInformationFormValue('whatsapp', '');
              }}
              color="secondary"
              type={ButtonGroupTypes.Button}
              className={'mt-2 w-full'}
            />
          </div>
        </div>
        {hasWhatsapp === false && (
          <FormInput<MothertContactInformationModel>
            label={`What cellphone number does ${details?.name} use for WhatsApp?`}
            register={motherFormRegister}
            nameProp={'whatsapp'}
            placeholder={'e.g 012 345 6789'}
            type={'number'}
            className="mt-4"
            value={existingContactInfo?.whatsapp}
            error={!!errors.whatsapp ? errors.whatsapp : undefined}
          ></FormInput>
        )}
      </div>
      <div className="flex h-full items-end">
        <Button
          type={'filled'}
          color={'primary'}
          className={'mt-4 w-full'}
          textColor={'white'}
          text={`Next`}
          icon={'ArrowCircleRightIcon'}
          iconPosition={'start'}
          onClick={() => {
            onSubmit(getMotherContactInformationFormValues());
          }}
          disabled={!isValid}
        />
      </div>
    </>
  );
};
